import type { ReactNode } from 'react';
import { Bars, Card, CardHeader, CardTitle, delta, type Bar } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { Friction, FrictionGroup, FrictionKind, FrictionKindName } from './friction-types';
import { screenWord } from '../usage/words';
import { CompanyName } from '../TestMark';
import { frictionDetailWords, frictionGroupWord, frictionKindWords, mostlyWord } from './words';
import '../admin.css';

/** Where a number leads: a kind's last ten, one company's page, or the Problems screen. */
export type FrictionTarget = { kind: FrictionKindName } | { tenant: string } | { problems: true };

export interface FrictionBoardProps {
  friction: Friction;
  /** The kind whose last ten are open, from the URL. */
  open: FrictionKindName | null;
  renderLink: (target: FrictionTarget, children: ReactNode) => ReactNode;
}

const TOP = 8;

function groupBars(kind: FrictionKind, f: Formatter): Bar[] {
  return kind.groups.slice(0, TOP).map((g) => ({
    label: frictionGroupWord(kind.kind, g),
    value: g.count,
    previous: g.previous,
    valueText: g.top ? `${f.int(g.count)} · ${mostlyWord(g.top)}` : f.int(g.count),
    href: kind.kind,
  }));
}

function LastTen({
  kind,
  f,
  renderLink,
}: {
  kind: FrictionKind;
  f: Formatter;
  renderLink: FrictionBoardProps['renderLink'];
}): ReactNode {
  if (kind.recent.length === 0) return <p className="ad-small">Nothing yet.</p>;
  return (
    <ol className="ad-list ad-friction__recent">
      {kind.recent.map((o) => (
        <li key={`${o.at}-${o.company?.id ?? 'anon'}`} className="ad-friction__occurrence">
          <span className="ad-small">{f.dateTime(o.at)}</span>
          <span>
            {o.company ? (
              <CompanyName
                name={renderLink({ tenant: o.company.slug }, o.company.name)}
                test={o.company.testCompany}
              />
            ) : (
              <span className="ad-small">Before sign-in</span>
            )}
          </span>
          <span className="ad-small">{screenWord(o.screen)}</span>
          <span>{frictionDetailWords(kind.kind, o)}</span>
        </li>
      ))}
    </ol>
  );
}

/** The kind's name, the count this period and how it moved (down is good). */
function KindHeader({ kind, f }: { kind: FrictionKind; f: Formatter }): ReactNode {
  const change = delta(kind.count, kind.previous, 'down');
  return (
    <CardHeader>
      <CardTitle>{frictionKindWords[kind.kind].title}</CardTitle>
      <span className="ad-row">
        <strong className="ad-friction__count">{f.int(kind.count)}</strong>
        <span className={`ad-today__delta ad-today__delta--${change.tone}`}>{change.text}</span>
        <span className="ad-small">vs the period before</span>
      </span>
    </CardHeader>
  );
}

/** Who to open: each group that belongs to one company, as a link to it; failed exports also lead to Problems. */
function Who({
  kind,
  f,
  renderLink,
}: {
  kind: FrictionKind;
  f: Formatter;
  renderLink: FrictionBoardProps['renderLink'];
}): ReactNode {
  const named = kind.groups.slice(0, TOP).filter((g) => g.company !== null);
  if (named.length === 0) return null;
  return (
    <ul
      className="ad-friction__who"
      aria-label={`${frictionKindWords[kind.kind].title}: who to open`}
    >
      {named.map((g: FrictionGroup) => (
        <li key={`${g.key}-${g.company!.slug}`}>
          <span>
            <CompanyName
              name={renderLink({ tenant: g.company!.slug }, g.company!.name)}
              test={g.company!.testCompany}
            />
            <span className="ad-small">
              {' '}
              · {frictionGroupWord(kind.kind, { ...g, company: null })}
            </span>
          </span>
          <span className="ad-small">
            {f.int(g.count)}
            {kind.kind === 'export.failed' ? (
              <> · {renderLink({ problems: true }, 'Retry or resolve in Problems')}</>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function KindCard({
  kind,
  open,
  renderLink,
}: {
  kind: FrictionKind;
  open: boolean;
  renderLink: FrictionBoardProps['renderLink'];
}): ReactNode {
  const f = useFormat();
  const words = frictionKindWords[kind.kind];
  const link = (href: string, children: ReactNode) =>
    renderLink({ kind: href as FrictionKindName }, children);
  return (
    <Card className="ad-friction__kind">
      <KindHeader kind={kind} f={f} />
      <p className="ad-small">{words.sentence}</p>
      {kind.groups.length === 0 ? <p className="ad-small">None this period.</p> : null}
      {kind.groups.length > 0 ? (
        <Bars
          bars={groupBars(kind, f)}
          label={`${words.title} by ${words.by}`}
          renderLink={link}
          format={f.int}
        />
      ) : null}
      <Who kind={kind} f={f} renderLink={renderLink} />
      <details className="ad-friction__drill" open={open}>
        <summary>The last ten</summary>
        <LastTen kind={kind} f={f} renderLink={renderLink} />
      </details>
    </Card>
  );
}

/** A kind nobody hit this period or the one before: it gets a name in one line, not a card. */
const quiet = (kind: FrictionKind): boolean => kind.count === 0 && kind.previous === 0;

/**
 * The problems people hit, ranked by how many this period; each with its groups, who to open, its trend and
 * its last ten. Kinds with nothing are one line under the cards, so the one that matters is not one of six
 * equal boxes.
 */
export function FrictionBoard({ friction, open, renderLink }: FrictionBoardProps): ReactNode {
  const f = useFormat();
  const ranked = [...friction.kinds].sort((a, b) => b.count - a.count || b.previous - a.previous);
  const loud = ranked.filter((k) => !quiet(k) || open === k.kind);
  const silent = ranked.filter((k) => quiet(k) && open !== k.kind);
  return (
    <div className="ad-stack">
      {loud.length === 0 ? <p className="ad-small">Nobody hit a problem this period.</p> : null}
      <div className="ad-friction">
        {loud.map((kind) => (
          <KindCard key={kind.kind} kind={kind} open={open === kind.kind} renderLink={renderLink} />
        ))}
      </div>
      {silent.length > 0 && loud.length > 0 ? (
        <p className="ad-friction__quiet">
          None this period or the one before:{' '}
          {f.list(silent.map((k) => frictionKindWords[k.kind].title.toLowerCase()))}.
        </p>
      ) : null}
    </div>
  );
}
